"use client";

import { FormEvent, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL, setStoredToken } from "@/lib/api-client";

type Mode = "signup" | "login";

type AuthCardProps = {
  mode: Mode;
};

const copy = {
  signup: {
    title: "Create your account",
    subtitle: "Free for individuals. Every answer cites its source.",
    buttonLabel: "Create account",
    endpoint: "register",
    passwordPlaceholder: "Create a secure password",
    successToast: "Account created! Redirecting…",
    footer: (
      <p className="text-body-sm text-muted">
        Already have an account?{" "}
        <Link href="/login" className="text-primary hover:underline">
          Log in
        </Link>
        .
      </p>
    ),
  },
  login: {
    title: "Welcome back",
    subtitle: "Log in to look up addresses and manage your account.",
    buttonLabel: "Log in",
    endpoint: "login",
    passwordPlaceholder: "Enter your password",
    successToast: "Welcome back!",
    footer: (
      <p className="text-body-sm text-muted">
        New to Prosa?{" "}
        <Link href="/signup" className="text-primary hover:underline">
          Create an account
        </Link>
        .
      </p>
    ),
  },
} satisfies Record<
  Mode,
  {
    title: string;
    subtitle: string;
    buttonLabel: string;
    endpoint: string;
    passwordPlaceholder: string;
    footer: React.ReactNode;
    successToast: string;
  }
>;

export function AuthCard({ mode }: AuthCardProps) {
  const router = useRouter();
  const { user, loading: authLoading, refresh } = useAuth();
  const { showToast } = useToast();

  const strings = useMemo(() => copy[mode], [mode]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      router.replace("/dashboard");
    }
  }, [authLoading, router, user]);

  // window.location rather than useSearchParams: the hook forces a Suspense
  // boundary on every page rendering this card (the reset-password lesson).
  useEffect(() => {
    if (mode === "login" && typeof window !== "undefined") {
      setSessionExpired(
        new URLSearchParams(window.location.search).get("session") === "expired",
      );
    }
  }, [mode]);

  if (authLoading) {
    return (
      <div className="mx-auto mt-16 max-w-md panel p-8 text-center text-body-sm text-muted">
        Checking your session…
      </div>
    );
  }

  if (user) {
    return (
      <div className="mx-auto mt-16 max-w-md panel p-8 text-center text-body-sm text-muted">
        Redirecting to your lookup…
      </div>
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      let token: string | null = null;

      if (mode === "signup") {
        const registerResponse = await fetch(`${API_BASE_URL}/auth/register`, {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ email, password }),
        });

        const registerPayload = await registerResponse
          .json()
          .catch(() => ({}));

        if (!registerResponse.ok) {
          setError(
            registerPayload.error ??
              registerPayload.detail ??
              (registerResponse.status >= 500
                ? "Server unavailable. Please try again shortly."
                : "Unable to create account. Try a different email."),
          );
          setSubmitting(false);
          return;
        }

        const loginResponse = await fetch(`${API_BASE_URL}/auth/login`, {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ email, password }),
        });

        const loginPayload = await loginResponse.json().catch(() => ({}));

        if (!loginResponse.ok) {
          setError(
            loginPayload.error ??
              loginPayload.detail ??
              "Account created, but login failed. Please try signing in.",
          );
          setSubmitting(false);
          return;
        }

        token = loginPayload.access_token ?? null;
      } else {
        const loginResponse = await fetch(`${API_BASE_URL}/auth/login`, {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ email, password }),
        });

        const loginPayload = await loginResponse.json().catch(() => ({}));

        if (!loginResponse.ok) {
          setError(
            loginPayload.error ??
              loginPayload.detail ??
              (loginResponse.status >= 500
                ? "Server unavailable. Please try again shortly."
                : "Authentication failed. Check your credentials and try again."),
          );
          setSubmitting(false);
          return;
        }

        token = loginPayload.access_token ?? null;
      }

      if (!token) {
        setError("Authentication succeeded but no token was returned.");
        setSubmitting(false);
        return;
      }

      setStoredToken(token);
      await refresh();
      showToast(strings.successToast);
      setEmail("");
      setPassword("");
      router.push("/dashboard");
    } catch (err) {
      console.error(err);
      setError("Server unavailable. Please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto mt-16 max-w-md space-y-6 panel p-8">
      {sessionExpired ? (
        <div className="rounded-[8px] border border-border bg-paper/60 px-4 py-3 text-body-sm text-muted">
          Your session expired — log in again to pick up where you left off.
        </div>
      ) : null}
      <div className="space-y-1.5 text-center">
        <h1 className="font-serif text-h2 font-semibold text-ink">
          {strings.title}
        </h1>
        <p className="text-body-sm text-muted">{strings.subtitle}</p>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="space-y-1.5">
          <label htmlFor="email" className="field-label">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="field"
            placeholder="you@example.com"
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <label htmlFor="password" className="field-label">
              Password
            </label>
            {mode === "login" && (
              <Link
                href="/forgot-password"
                className="text-body-sm text-muted hover:text-primary hover:underline"
              >
                Forgot password?
              </Link>
            )}
          </div>
          <input
            id="password"
            type="password"
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            required
            minLength={8}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="field"
            placeholder={strings.passwordPlaceholder}
          />
        </div>
        <Button type="submit" className="w-full" size="lg" disabled={submitting}>
          {submitting ? "Authenticating…" : strings.buttonLabel}
        </Button>
      </form>

      {error && (
        <div className="rounded-[8px] border border-warn/30 bg-warn/[0.06] p-3 text-body-sm text-warn">
          {error}
        </div>
      )}
      {strings.footer}
    </div>
  );
}

